import { useEffect, useId, useState } from "react";
import {
  Box,
  BoxProps,
  FormControl,
  InputLabel,
  Typography,
  TypographyProps,
} from "@mui/material";
import "katex/dist/katex.css";
import { isDefined } from "@app/utils";

interface CarKatexProps extends Omit<BoxProps, "children"> {
  equation: string;
  displayMode?: boolean;
}

export const CarKatex = ({
  equation,
  displayMode = true,
  ...props
}: CarKatexProps) => {
  const [katexHtml, setKatexHtml] = useState("");

  useEffect(() => {
    (async () => {
      const { renderToString } = await import("katex");

      setKatexHtml(
        renderToString(equation, {
          displayMode,
          output: "htmlAndMathml",
          errorColor: "#f55",
          throwOnError: false,
        }),
      );
    })();
  }, [equation, displayMode]);

  return (
    <Box
      {...props}
      component="span"
      sx={{
        fontSize: "20px",
        ...props.sx,
        color: "softBlack",
        ".katex-display": {
          margin: 0,
        },
      }}
      dangerouslySetInnerHTML={{
        __html: katexHtml,
      }}
    />
  );
};

export interface CarTextCalcFieldProps {
  className?: string;
  sx?: BoxProps["sx"];
  value?: string | null;
  label?: string;
  valueProps?: TypographyProps;
}

export const CarKatexCalcField = ({
  className,
  sx,
  label,
  value,
  valueProps,
}: CarTextCalcFieldProps) => {
  const id = useId();
  return (
    <FormControl className={className} sx={sx}>
      {label ? <InputLabel htmlFor={id}>{label}</InputLabel> : undefined}
      <Box
        id={id}
        sx={{
          mt: label ? "24px" : undefined,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          textAlign="left"
          variant="par01Regular"
          {...valueProps}
          sx={{ ml: 1, color: "black", ...valueProps?.sx }}
        >
          {isDefined(value) ? (
            <CarKatex
              sx={{ fontSize: "16px" }}
              displayMode={false}
              equation={value}
            />
          ) : (
            "-"
          )}
        </Typography>
      </Box>
    </FormControl>
  );
};
